import { Link } from '@inertiajs/react';
import { useEffect, useRef, useState } from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Edit, Trash2, Plus } from 'lucide-react';
import { PaginatedTableControls } from '@/components/ui/paginated-table';
import { useToast } from '@/hooks/useToast';
import { fetchJson, PaginationMeta, RequestSequencer } from '@/lib/jsonPagination';

interface Job {
  id: number;
  job_id: string;
  name?: string;
  status: string;
}

interface JobsTableProps {
  title: string;
  jobs: Job[];
  documentId: number;
  jobType: 'parse' | 'extract' | 'split';
  onDelete: (id: number) => void;
  onDeleteAll?: () => void;
  pagination?: PaginationMeta;
}

export function JobsTable({ title, jobs, documentId, jobType, onDelete, onDeleteAll, pagination }: JobsTableProps) {
  const [visibleJobs, setVisibleJobs] = useState(jobs);
  const [paginationMeta, setPaginationMeta] = useState(pagination);
  const [isFetchingPage, setIsFetchingPage] = useState(false);
  const fetchSequencer = useRef(new RequestSequencer());
  const { toast } = useToast();

  useEffect(() => {
    setVisibleJobs(jobs);
    setPaginationMeta(pagination);
  }, [jobs, pagination]);

  const getStatusBadge = (status: string) => {
    const variants = {
      completed: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      failed: 'bg-red-100 text-red-800',
    } as const;
    
    return variants[status as keyof typeof variants] || 'bg-gray-100 text-gray-800';
  };

  const newJobPath = `/documents/${documentId}/${jobType}_jobs/new`;
  const editJobPath = (jobId: number) => `/documents/${documentId}/${jobType}_jobs/${jobId}/edit`;
  const canFetchPages = jobType === 'parse' || jobType === 'extract';

  const fetchJobsPage = async (page: number, perPage = paginationMeta?.per_page || 25) => {
    if (!canFetchPages) return;

    setIsFetchingPage(true);
    const request = fetchSequencer.current.start();
    const result = await fetchJson<{ jobs: Job[]; pagination: PaginationMeta }>(
      `/documents/${documentId}/${jobType}_jobs.json?page=${page}&per_page=${perPage}`
    );

    if (!result.ok) {
      if (request.isCurrent()) {
        setIsFetchingPage(false);
        toast({
          title: 'Error',
          description: `Failed to load ${title.toLowerCase()}.`,
          variant: 'destructive',
        });
      }
      return;
    }

    if (!request.isCurrent()) return;

    setIsFetchingPage(false);
    setVisibleJobs(result.data.jobs);
    setPaginationMeta(result.data.pagination);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {visibleJobs.length > 0 ? (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Job ID</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleJobs.map((job) => (
                  <TableRow key={job.id}>
                    <TableCell>
                      {job.name && <div className="font-semibold">{job.name}</div>}
                      <div className="text-gray-600">{job.job_id}</div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusBadge(job.status)}>
                        {job.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button asChild size="sm" variant="outline">
                          <Link href={editJobPath(job.id)}>
                            <Edit className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => onDelete(job.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            
            <div className="flex justify-center gap-3 mt-6">
              <Button asChild className="bg-blue-500 hover:bg-blue-600">
                <Link href={newJobPath}>
                  <Plus className="h-4 w-4 mr-2" />
                  New {title.slice(0, -1)} {/* Remove 's' from title */}
                </Link>
              </Button>
              {onDeleteAll && (
                <Button
                  onClick={onDeleteAll}
                  variant="destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete All {title}
                </Button>
              )}
            </div>

            {paginationMeta && paginationMeta.total_count > 0 && !isFetchingPage && (
              <PaginatedTableControls
                currentPage={paginationMeta.current_page}
                totalPages={paginationMeta.total_pages}
                perPage={paginationMeta.per_page}
                totalCount={paginationMeta.total_count}
                onPageChange={(page) => fetchJobsPage(page)}
                onPerPageChange={(perPage) => fetchJobsPage(1, perPage)}
              />
            )}
          </>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <p>No {title.toLowerCase()} found.</p>
            <Button asChild className="mt-4 bg-blue-500 hover:bg-blue-600">
              <Link href={newJobPath}>
                <Plus className="h-4 w-4 mr-2" />
                Create First {title.slice(0, -1)} {/* Remove 's' from title */}
              </Link>
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
